// Dev-only ledger of analytics events flowing through `lib/analytics`.
// Mirrors the api-registry / helius-registry shape (capped ring buffer +
// `useSyncExternalStore` subscribe/snapshot) but for our analytics
// abstraction rather than HTTP.
//
// === What it captures ===
//
// Every call to the public `analytics` API is recorded:
//   - `event`      — `trackEvent(name, props)` — including the auto-
//                    injected `network` tag from `setAnalyticsNetwork`.
//   - `identify`   — `identifyUser(userId, attrs)` — including the
//                    `network` tag re-applied on every network toggle.
//   - `gate`       — `checkGate(name)` with the resolved boolean +
//                    source (`override` / `provider`).
//   - `experiment` — `getExperiment(name)` with `value` + `ruleID`.
//   - `config`     — `getConfig(name)` with the returned dict.
//
// Each record carries a `delivery` field — `sent` when the active
// provider is the real Statsig provider, `noop` when it's the no-op
// provider (local dev, missing client key). Lets engineers see what
// the app intends to send vs. what's actually reaching Statsig.
//
// === Why the registry runs in production too ===
//
// Unlike api-registry / helius-registry (which gate on NODE_ENV and
// rely on DCE), the analytics registry must record on Vercel
// production builds — that's where engineers (`@morfi.markets` users)
// actually need to verify Statsig is receiving the right events with
// the right network tag. The dev toolkit itself is gated on internal
// email at runtime, so the panel only renders for engineers; the
// registry is shared, capped (500 records, ~25MB worst case), and
// global-state-bound, so the per-user memory cost on non-internal
// sessions is negligible. Server-side rendering still no-ops via the
// `typeof window !== 'undefined'` guard.

const IS_BROWSER: boolean = typeof window !== 'undefined';

const MAX_BUFFER_SIZE = 500;
/** Cap captured prop / result strings so a misbehaved caller can't
 *  OOM the panel. 50KB / record × 500 records ≈ 25MB worst case. */
export const MAX_PAYLOAD_BYTES = 50_000;

export type EventKind = 'event' | 'identify' | 'gate' | 'experiment' | 'config' | 'system';

export type EventDelivery = 'sent' | 'noop' | 'system';

export type GateSource = 'override' | 'provider';

export interface AnalyticsRecord {
  readonly id: number;
  readonly timestamp: number;
  readonly kind: EventKind;
  readonly delivery: EventDelivery;
  /** Event name, gate name, experiment name, config name, or userId
   *  (for `identify`). System records use this as a short label. */
  readonly name: string;
  /** Network tag at record time. Pulled from the analytics module's
   *  `currentNetwork` if known, otherwise null. Useful for confirming
   *  the toggle landed before an event fired. */
  readonly network: string | null;
  /** For `event` and `identify`: the full enriched props (already
   *  including the auto-injected `network` tag). For `gate`:
   *  `{ result: boolean, source: GateSource }`. For `experiment`:
   *  `{ value, ruleID }`. For `config`: the returned dict. For
   *  `system`: null. */
  readonly payload: Record<string, unknown> | null;
  /** Optional human-readable message — used by `system` records. */
  readonly message: string | null;
}

export interface AnalyticsSnapshot {
  readonly totalEvents: number;
  readonly totalIdentifies: number;
  readonly totalGates: number;
  readonly totalRecords: number;
  readonly sentCount: number;
  readonly noopCount: number;
  readonly startedAt: number;
  readonly records: readonly AnalyticsRecord[];
}

interface MutableRecord {
  id: number;
  timestamp: number;
  kind: EventKind;
  delivery: EventDelivery;
  name: string;
  network: string | null;
  payload: Record<string, unknown> | null;
  message: string | null;
}

interface RegistryState {
  buffer: MutableRecord[];
  listeners: Set<() => void>;
  startedAt: number;
  totalEvents: number;
  totalIdentifies: number;
  totalGates: number;
  sentCount: number;
  noopCount: number;
  idSeq: number;
  cachedSnapshot: AnalyticsSnapshot;
}

interface AnalyticsGlobals {
  __morfiAnalyticsLedger?: RegistryState;
}

const NOOP_SNAPSHOT: AnalyticsSnapshot = Object.freeze({
  totalEvents: 0,
  totalIdentifies: 0,
  totalGates: 0,
  totalRecords: 0,
  sentCount: 0,
  noopCount: 0,
  startedAt: 0,
  records: [],
});

const NOOP_UNSUBSCRIBE = (): void => {};

let _state: RegistryState | null = null;

function ensureState(): RegistryState {
  if (_state !== null) return _state;
  const g = globalThis as typeof globalThis & AnalyticsGlobals;
  const existing = g.__morfiAnalyticsLedger;
  if (existing !== undefined) {
    _state = existing;
    return existing;
  }
  const fresh: RegistryState = {
    buffer: [],
    listeners: new Set(),
    startedAt: Date.now(),
    totalEvents: 0,
    totalIdentifies: 0,
    totalGates: 0,
    sentCount: 0,
    noopCount: 0,
    idSeq: 0,
    cachedSnapshot: NOOP_SNAPSHOT,
  };
  fresh.cachedSnapshot = freezeSnapshotFrom(fresh);
  g.__morfiAnalyticsLedger = fresh;
  _state = fresh;
  return fresh;
}

function freezeSnapshotFrom(s: RegistryState): AnalyticsSnapshot {
  const records: AnalyticsRecord[] = s.buffer.map(
    (r): AnalyticsRecord => ({
      id: r.id,
      timestamp: r.timestamp,
      kind: r.kind,
      delivery: r.delivery,
      name: r.name,
      network: r.network,
      payload: r.payload,
      message: r.message,
    }),
  );
  return {
    totalEvents: s.totalEvents,
    totalIdentifies: s.totalIdentifies,
    totalGates: s.totalGates,
    totalRecords: records.length,
    sentCount: s.sentCount,
    noopCount: s.noopCount,
    startedAt: s.startedAt,
    records,
  };
}

function notify(s: RegistryState): void {
  for (const l of s.listeners) {
    l();
  }
}

/** Truncate a payload string-form so the display layer never has to
 *  worry about megabyte-sized blobs. Applied to the JSON serialization
 *  in the UI, not to the live object — the object stays correct in
 *  memory in case future filters need to read it. */
export function truncatePayload(json: string): string {
  if (json.length <= MAX_PAYLOAD_BYTES) return json;
  return `${json.slice(0, MAX_PAYLOAD_BYTES)}\n…[truncated, ${String(
    json.length - MAX_PAYLOAD_BYTES,
  )}B more]`;
}

export interface RecordAnalyticsArgs {
  readonly kind: EventKind;
  readonly delivery: EventDelivery;
  readonly name: string;
  readonly network: string | null;
  readonly payload: Record<string, unknown> | null;
  readonly message?: string | null;
}

export function recordAnalytics(args: RecordAnalyticsArgs): void {
  if (!IS_BROWSER) return;
  const s = ensureState();
  s.idSeq += 1;
  if (args.kind === 'event') s.totalEvents += 1;
  else if (args.kind === 'identify') s.totalIdentifies += 1;
  else if (args.kind === 'gate') s.totalGates += 1;
  if (args.delivery === 'sent') s.sentCount += 1;
  else if (args.delivery === 'noop') s.noopCount += 1;

  const record: MutableRecord = {
    id: s.idSeq,
    timestamp: Date.now(),
    kind: args.kind,
    delivery: args.delivery,
    name: args.name,
    network: args.network,
    payload: args.payload,
    message: args.message ?? null,
  };
  s.buffer.push(record);
  if (s.buffer.length > MAX_BUFFER_SIZE) {
    s.buffer.shift();
  }
  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

/** Insert a synthetic `system` record — used to pin the install marker
 *  ("analytics provider: statsig") at the bottom of the ledger so the
 *  engineer can confirm wiring without console noise. */
export function recordAnalyticsSystem(message: string): void {
  if (!IS_BROWSER) return;
  const s = ensureState();
  s.idSeq += 1;
  const record: MutableRecord = {
    id: s.idSeq,
    timestamp: Date.now(),
    kind: 'system',
    delivery: 'system',
    name: 'system',
    network: null,
    payload: null,
    message,
  };
  s.buffer.push(record);
  if (s.buffer.length > MAX_BUFFER_SIZE) {
    s.buffer.shift();
  }
  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

export function subscribeAnalyticsRegistry(listener: () => void): () => void {
  if (!IS_BROWSER) return NOOP_UNSUBSCRIBE;
  const s = ensureState();
  s.listeners.add(listener);
  return (): void => {
    s.listeners.delete(listener);
  };
}

export function getAnalyticsSnapshot(): AnalyticsSnapshot {
  if (!IS_BROWSER) return NOOP_SNAPSHOT;
  return ensureState().cachedSnapshot;
}

export function clearAnalyticsStats(): void {
  if (!IS_BROWSER) return;
  const s = ensureState();
  s.buffer = [];
  s.totalEvents = 0;
  s.totalIdentifies = 0;
  s.totalGates = 0;
  s.sentCount = 0;
  s.noopCount = 0;
  s.startedAt = Date.now();
  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

export function formatAnalyticsSnapshot(snapshot: AnalyticsSnapshot, now: number): string {
  const lines: string[] = [];
  const elapsedS = Math.max(1, Math.floor((now - snapshot.startedAt) / 1000));
  const ratePerMin = ((snapshot.totalEvents / elapsedS) * 60).toFixed(1);
  lines.push(`# Analytics ledger`);
  lines.push(`captured: ${new Date(now).toISOString()}`);
  lines.push(
    `events: ${String(snapshot.totalEvents)} ` +
      `(sent ${String(snapshot.sentCount)} / noop ${String(snapshot.noopCount)})` +
      ` · identifies: ${String(snapshot.totalIdentifies)}` +
      ` · gates: ${String(snapshot.totalGates)}` +
      ` over ${String(elapsedS)}s (~${ratePerMin}/min events)`,
  );
  lines.push('');
  lines.push('## Records (newest first)');
  for (let i = snapshot.records.length - 1; i >= 0; i -= 1) {
    const r = snapshot.records[i];
    if (r === undefined) continue;
    const tag = r.network !== null ? ` network=${r.network}` : '';
    const delivery = r.delivery !== 'system' ? ` (${r.delivery})` : '';
    const payloadStr = r.payload !== null ? ` ${truncatePayload(safeStringify(r.payload))}` : '';
    const msg = r.message !== null ? ` — ${r.message}` : '';
    lines.push(
      `- \`${formatTime(r.timestamp)}\` **${r.kind}**${delivery} ${r.name}${tag}${msg}${payloadStr}`,
    );
  }
  return lines.join('\n');
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

/** Values JSON.stringify may hand to a replacer (including non-JSON extras). */
type ReplacerSeenValue =
  | string
  | number
  | boolean
  | null
  | bigint
  | ((...args: never[]) => string | number | boolean | null | object)
  | object;

type ReplacerReturn = string | number | boolean | null | object | undefined;

/** Stringify with circular-reference handling. The analytics props
 *  are normally pure JSON, but components occasionally pass a Date or
 *  a class instance — don't crash the whole panel for that. */
export function safeStringify<T>(value: T): string {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(
      value,
      (_k: string, v: ReplacerSeenValue): ReplacerReturn => {
        if (typeof v === 'object' && v !== null) {
          if (seen.has(v)) return '[Circular]';
          seen.add(v);
        }
        if (typeof v === 'bigint') return `${v.toString()}n`;
        if (typeof v === 'function') return '[Function]';
        if (v instanceof Date) return v.toISOString();
        return v;
      },
      2,
    );
  } catch {
    // SILENT-CATCH: dev panel must not crash on a weird payload.
    return String(value);
  }
}

/** Test-only — wipes registry state. Not exported from the public barrel. */
export function __resetAnalyticsRegistryForTests(): void {
  _state = null;
  const g = globalThis as typeof globalThis & AnalyticsGlobals;
  delete g.__morfiAnalyticsLedger;
}
